/**
 * Handing a narrow viewport off to the separate mobile app.
 *
 * The editor used to do this unconditionally below 1024px, to an origin that
 * fell back to `http://localhost:3002`. Nothing in this workspace serves that
 * port and `VITE_MOBILE_ORIGIN` is set nowhere, so every viewport at or below
 * 1024px — every phone, every tablet, and a merely narrow desktop window —
 * hard-navigated to a blank document. The URL also carried `?mode=block`, a
 * mode removed in web #236.
 *
 * So the hand-off is opt-in now. Configure an origin and it happens; leave it
 * unset and the editor renders where it is. A cramped layout is worth more than
 * a blank one, and a fallback that points at a port nobody runs is not a
 * fallback.
 *
 * Kept as a pure function because the caller redirects on the spot — the choice
 * has to be testable without a browser that is about to navigate away.
 */

/** Below this, the desktop workspace is too tight and a mobile app would do better. */
export const MOBILE_MAX_WIDTH = 1024;

export interface MobileHandoffInput {
  /** VITE_MOBILE_ORIGIN. Absent or blank means there is no mobile app to go to. */
  origin: string | undefined | null;
  /** Where we are now, so we never bounce a page to itself. */
  href: string;
  /** Whether the viewport is at or below MOBILE_MAX_WIDTH. */
  isNarrow: boolean;
  documentId: string;
}

/**
 * Where to send this document, or null to stay put.
 *
 * Null is the answer far more often than it used to be, and that is the fix.
 */
export function mobileHandoffTarget({
  origin,
  href,
  isNarrow,
  documentId,
}: MobileHandoffInput): string | null {
  const base = (origin ?? "").trim().replace(/\/+$/, "");
  if (!base) return null;              // no mobile app configured
  if (!isNarrow) return null;          // wide enough for the desktop workspace
  if (!documentId) return null;
  if (href.startsWith(base)) return null;  // already there; do not loop

  // No ?mode=block. Block mode was removed in web #236, so passing it asked the
  // mobile app for something that no longer exists.
  return `${base}/document/${documentId}`;
}
